import { useState } from "react";
import { AlertTriangle, X, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { sfx } from "./audio";

// Persisted mute/unmute widget — toggles the whole game soundboard (BGM + SFX).
export const SpaceMuteBtn = ({ testid = "space-mute-btn" }) => {
  const [muted, setMuted] = useState(sfx.isMuted());
  return (
    <button
      className={`ss-mute-btn ${muted ? "muted" : ""}`}
      onClick={() => setMuted(sfx.toggle())}
      data-testid={testid}
      aria-label={muted ? "Unmute audio" : "Mute audio"}
    >
      {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
    </button>
  );
};

// High-tech hexagonal Home Routing icon — persistent view-router shortcut that
// slides the interface straight back to the Sanctuary base grid (no reload).
export const SpaceHomeBtn = ({ onClick, testid = "space-home-router" }) => (
  <button className="ss-home-router" onClick={onClick} data-testid={testid} aria-label="Return to Sanctuary base grid">
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M12 1.6 21 6.8 V17.2 L12 22.4 3 17.2 V6.8 Z" stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
      <path d="M12 6.4 17 10.3 V16 H13.6 V12.7 H10.4 V16 H7 V10.3 Z" fill="currentColor" />
      <circle cx="12" cy="12" r="10.4" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
    </svg>
    <span className="ss-home-router-glow" />
  </button>
);

// Reset Game control — manual tap + DOUBLE confirmation before the server drops
// and re-seeds the player's colony to the baseline startup configuration.
export const ResetGameControl = ({ onReset }) => {
  const [stage, setStage] = useState(0); // 0 idle · 1 first confirm · 2 final confirm
  const [busy, setBusy] = useState(false);

  const doReset = async () => {
    setBusy(true);
    try { await onReset(); } finally { setBusy(false); setStage(0); }
  };

  return (
    <>
      <button className="ss-reset-btn" onClick={() => setStage(1)} data-testid="space-reset-btn">
        <RotateCcw size={13} /> Reset Game
      </button>

      {stage > 0 && (
        <div className="ss-confirm" onClick={() => !busy && setStage(0)} data-testid="space-reset-modal">
          <div className="ss-confirm-box" onClick={(e) => e.stopPropagation()}>
            <button className="ss-confirm-x" onClick={() => !busy && setStage(0)} data-testid="space-reset-cancel"><X size={16} /></button>
            <span className="ss-confirm-icon"><AlertTriangle size={26} /></span>
            {stage === 1 ? (
              <>
                <span className="ss-confirm-t">Reset your entire colony?</span>
                <span className="ss-confirm-s">Command Centre drops to Lv 1, your Titanium wallet and all structures are wiped, and your hero deck returns to the startup squad. This cannot be undone.</span>
                <button className="ss-confirm-go" onClick={() => setStage(2)} data-testid="space-reset-confirm-1">Continue</button>
              </>
            ) : (
              <>
                <span className="ss-confirm-t danger">Final confirmation</span>
                <span className="ss-confirm-s">This is permanent. Tap below to wipe and re-seed your game from scratch.</span>
                <button className="ss-confirm-go danger" onClick={doReset} disabled={busy} data-testid="space-reset-confirm-2">
                  {busy ? "Resetting…" : "Wipe & Restart"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
